import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";

import localizeHref from "../../../../lib/multilingual/localizeHref";
import { IconStateInformation } from "@/assets/icons/figma/IconStateInformation";
import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import ProductUtils from "@/components/ProductDetail/utils/ProductUtils";
import { isUsLocale } from "@/i18n/config";
import { InfoPanel } from "@/ui/InfoPanel";
import { Flavor } from "@/ui/typography/Flavor";

/**
 * WHO-6510. On `/en-us`, a listing that can't ship to the US says so before the buyer reaches
 * checkout (which refuses it), and points at its category, which on `/en-us` lists only what does
 * ship. `usShippable` is the gateway's answer (WHO-6498): the same rule as the search filter and
 * checkout, so the three never disagree.
 *
 * Nothing on any other locale, and nothing on a sold listing, which no one can buy anyway.
 */
export const ProductDetailsUsShippingNotice = ({
  product,
}: {
  product: ProductBySlugNonNullable;
}) => {
  const { t } = useTranslation();
  const { locale } = useRouter();

  /* `usShippable` is only selected on /en-us (`@include(if: $usMarket)`): absent means unknown. */
  if (
    !isUsLocale(locale) ||
    product.usShippable !== false ||
    ProductUtils.isSold(product)
  ) {
    return null;
  }

  return (
    <InfoPanel
      className="w-full"
      icon={<IconStateInformation className="size-5 self-start" />}
      body={
        <div className="flex flex-col gap-1" data-testid="us-shipping-notice">
          <Flavor size="medium" className="font-bold">
            {t("pdp:pdp-us-not-shippable-title", {
              defaultValue: "This item doesn't ship to the US",
            })}
          </Flavor>
          <Link
            href={similarItemsHref(product)}
            className="font-body text-sm text-brand-500 underline"
          >
            {t("pdp:pdp-us-not-shippable-similar", {
              defaultValue: "See similar items that do",
            })}
          </Link>
        </div>
      }
    />
  );
};

/**
 * The listing's own category: `categories` runs from the most specific up, as the breadcrumbs
 * read it. `route` is the Dutch path; `/en-us` uses the English one. No category: the US home.
 */
export const similarItemsHref = (
  product: Pick<ProductBySlugNonNullable, "categories">
): string => {
  const route = product.categories?.[0]?.route;

  return route ? localizeHref(`/${route}`, "nl", "en") : "/";
};
